import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import React, { memo } from 'react';

import useAppTheme from '../../util/hooks/useAppTheme';
import { FormattedMessage } from '../../util/reactIntl';
import { startupScreenSx } from '../CookieConsent/startupScreenSx';
import Link from '../Links/Link/Link';

const linkSx = {
  '&:hover': {
    textDecoration: 'none',
  },
};

// The translation contains blank lines separating the links from the copy.
const textSx = { whiteSpace: 'pre-line' };

/**
 * ToS/app-usage startup screen shown on first app launch, right before the
 * cookie-consent startup screen — both share the same full-screen takeover
 * look (startupScreenSx). Accepting is the only way past it, so there is no
 * separate close affordance.
 *
 * @component
 * @param {object} props
 * @param {boolean} props.isOpen - Controls whether the screen is displayed
 * @param {Function} props.onClick - Handler called when the accept button is tapped
 * @returns {React.ReactNode} The full-screen ToS acceptance screen
 */
const AppUsageModalComponent = ({ isOpen, onClick }) => {
  const theme = useAppTheme();

  if (!isOpen) return null;

  const termsOfServiceLink = (
    <Link sx={linkSx} to={{ name: 'StoryblokPage', params: { slug: 'terms-of-service' } }}>
      <FormattedMessage id="PortalModalContainer.appUsageModal.termsOfService" />
    </Link>
  );

  const privacyPolicyLink = (
    <Link sx={linkSx} to={{ name: 'StoryblokPage', params: { slug: 'privacy-policy' } }}>
      <FormattedMessage id="PortalModalContainer.appUsageModal.privacyPolicy" />
    </Link>
  );

  return (
    <Box sx={{ ...startupScreenSx, zIndex: theme.zIndex.modal }}>
      <Typography fontWeight="bold" mb={1} variant="h5">
        <FormattedMessage id="PortalModalContainer.appUsageModal.title" />
      </Typography>
      <Typography mb={2} sx={textSx} variant="body2">
        <FormattedMessage
          id="PortalModalContainer.appUsageModal.text"
          values={{
            termsOfServiceLink: termsOfServiceLink,
            privacyPolicyLink: privacyPolicyLink,
          }}
        />
      </Typography>
      <Button color="secondary" fullWidth onClick={onClick} variant="contained">
        <FormattedMessage id="PortalModalContainer.appUsageModal.buttonText" />
      </Button>
    </Box>
  );
};

AppUsageModalComponent.displayName = 'AppUsageModal';

const AppUsageModal = memo(AppUsageModalComponent);

export default AppUsageModal;
